<template>
  <div class="d-flex flex-column w-100" style="min-height: 12.5625rem">
    <b-row align-v="center">
      <b-col cols="12" lg="6">
        <p class="text-muted fs-20 p-0 mb-0" style="line-height: 0.675rem;">Indicadores</p>
        <!-- TODO: Change this date!! -->
        <small class="mb-0" style="color: #1d58b7">
          Ultima actualización:
          <span v-if="loadingDashboards">
            consultando datos
            <font-awesome-icon :icon="['fas', 'spinner']" pulse fixed-width />
          </span>
          <span v-else-if="error" class="text-danger">
            Hubo un error al consultar los datos a SAP, intente nuevamente
          </span>
          <span v-else-if="dashboardsState.lastFetchedAt">{{ dashboardsState.lastFetchedAt | formatDateTime }}</span>
          <span v-else>Sin ultima actualización</span>
        </small>
      </b-col>
      <b-col cols="12" lg="6" class="text-right">
        <b-button size="sm" :variant="activeSection === 'general' ? 'info' : 'outline-secondary'" @click="toggleActiveSection('general')">
          <font-awesome-icon :icon="['far', 'info-circle']" fixed-width />
          Vista general
        </b-button>
        <b-button size="sm" :variant="activeSection === 'tendencies' ? 'info' : 'outline-secondary'" @click="toggleActiveSection('tendencies')">
          <font-awesome-icon :icon="['far', 'chart-line']" fixed-width />
          Gráfica
        </b-button>
        <b-button
          size="sm"
          variant="dark"
          class="ml-3"
          v-b-tooltip.hover title="Traer ultima actualización de datos de SAP"
          :disabled="loadingDashboards"
          @click="fetchDashboards(true)"
        >
          <!-- TODO: Add method to sync, add loading spinner, add  -->
          <font-awesome-icon :icon="['far', 'sync']" fixed-width />
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="activeSection === 'general'" align-content="center" class="text-center flex-grow-1 dashboard-container">
      <b-col cols="12" style="position: relative;">
        <Carousel>
          <DashboardCard
            v-for="dashboard in formattedDashboards"
            :key="dashboard.title"
            :title="dashboard.title"
            :badge="dashboard.badge"
            :badgeColor="dashboard.badgeColor"
            :description="dashboard.description"
            :value="dashboard.value"
            :icon="dashboard.icon"
            :variant="dashboard.variant"
            :loading="(!ready || isLoading) || loadingDashboards"
            :error="error"
            @open-detail-modal="openDetailModal(dashboard)"
          />
          <div v-if="!isLoading && error" class="position-absolute w-100"  style="top: 60%; left: 50%; transform: translate(-50%, -50%); z-index: 11;">
            <p class="text-center text-danger mb-1">No se pudieron cargar los datos de SAP</p>
            <b-button variant="outline-danger" @click="fetchDashboards(true)">
              <font-awesome-icon :icon="['far', 'sync']" fixed-width />
              <span class="pl-1">Volver a cargar</span>
            </b-button>
          </div>
        </Carousel>
      </b-col>
    </b-row>

    <b-row v-else align-content="center" class="text-center flex-grow-1 dashboard-container">
      <b-col>
        <b-overlay :show="loadingDashboards || error" rounded="sm">
          <template #overlay>
            <div class="d-flex justify-content-center align-items-center w-100 fs-19">
              <p v-if="!ready || loadingDashboards" class="text-center text-muted">
                Consultando datos
              </p>

              <!-- <p v-else-if="Object.keys(dashboardsState.dashboardCharts).length === 0" class="text-center text-muted">
                No hay datos para mostrar
              </p> -->

              <div v-else-if="error" class="text-center text-danger mb-1" style="min-width: 210px;">
                <p>No se pudieron cargar los datos</p>
                <b-button variant="outline-danger" @click="fetchDashboards(true)">
                  <font-awesome-icon :icon="['far', 'sync']" fixed-width />
                  <span class="pl-1">Volver a cargar</span>
                </b-button>
              </div>
            </div>
          </template>

          <ClientLineChart
            :chart-data="dashboardsState.dashboardCharts"
            :labels="lastTwelveMonths"
          />
        </b-overlay>
      </b-col>
    </b-row>

    <!-- Details Modal -->
    <b-modal
      v-model="showDetailModal"
      @hide="onHideDetailModal"
      scrollable
      size="xl"
      class="p-0"
      hide-footer
    >
      <template #modal-title>
        <span class="font-weight-bold fs-20" style="color: #1d58b7">
          <font-awesome-icon :icon="currentDashboard.icon || ['far', 'info-circle']" fixed-width />
          Detalle de {{ currentDashboard.title }} del ultimo año
        </span>
      </template>
      <b-row align-h="between">
        <b-col cols="12">
          <p v-if="currentDashboard.id == 'Invoices' && dashboardsState.dashboardCards.OverdueInvoice === 'Y'" class="text-danger text-right m-0 fs-14">
            <font-awesome-icon :icon="['far', 'info-circle']" fixed-width />
            Cliente cuenta con facturas vencidas
          </p>
          <b-form-group style="width: 100%" class="mb-0 mt-2">
            <b-input-group>
              <b-input
                v-model="searchTerm"
                :placeholder="searchBardPlaceHolders[currentDashboard.id] || ''"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary" @click="searchTerm = ''">
                  Limpiar
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row align-h="end">
        <b-col>
          <b-table
            :items="detailItems"
            :fields="fields"
            :busy="loadingDashboards"
            outlined
            show-empty
            sticky-header="60vh"
            :tbody-tr-class="rowClass"
            class="mt-1 fs-14 modal-table"
          >
            <template #empty>
              <div class="text-danger text-center text-info my-2">
                <span>No hay resultados de {{currentDashboard.title}}</span>
              </div>
            </template>
            <template v-slot:cell(DocDate)="data">
              <span>{{ data.value | formatDate }}</span>
            </template>
            <template v-slot:cell(DocTotalSy)="data">
              <span>{{ data.value | dollarize }}</span>
            </template>
            <template v-slot:cell(OpenTotal)="data">
              <span>{{ data.value | dollarize }}</span>
            </template>
            <template v-slot:cell(PayToDate)="data">
              <span>{{ data.value | dollarize }}</span>
            </template>
            <template v-slot:cell(Title)="data">
              <span v-if="data.value">{{ data.value }}</span>
              <span v-else class="text-muted">Sin titulo</span>
            </template>
            <template v-slot:cell(DocDueDate)="data">
              <span>{{ data.value | formatDate }}</span>
            </template>
            <template v-slot:cell(TargetDate)="data">
              <span>{{ data.value | formatDate }}</span>
            </template>
            <template v-slot:cell(Status)="data">
              <span>{{ data.value }}</span>
            </template>
            <template #table-busy>
              <div class="text-center text-info my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Cargando...</strong>
              </div>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <small class="mb-0 float-right" style="color: #1d58b7">
            Ultima actualización:
            <span v-if="dashboardsState.lastFetchedAt">{{ dashboardsState.lastFetchedAt | formatDateTime }}</span>
            <span v-else>Sin ultima actualización</span>
          </small>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DashboardCard from './DashboardCard'
import Carousel from '../../shared/Carousel'
import ClientLineChart from './ClientLineChart'

export default {
  name: 'ClientDashboards',
  components: { DashboardCard, Carousel, ClientLineChart },
  props: {
    ready: {
      type: Boolean,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      id: Number(this.$route.params.id),
      activeSection: 'general',
      loadingDashboards: false,
      error: false,
      dashboardsConfig: {
        'Quotes': { position: 1, id: 'Quotes', title: 'Cotizaciones', badge: 'Abiertas', badgeColor:'success', description: 'Cotizaciones', icon: ['fas', 'calculator'], value: 0 },
        'Orders': { position: 2, id: 'Orders', title: 'OI | OR | OM', badge: 'Abiertas', badgeColor:'success', description: 'Ordenes', icon: ['far', 'file-alt'], value: 0 },
        'ServiceCalls': { position: 3, id: 'ServiceCalls', title: 'OS', badge: 'Abiertas', badgeColor:'success', description: 'Ordenes de servicio', icon: ['far', 'ballot-check'], value: 0 },
        'ServiceContracts': { position: 4, id: 'ServiceContracts', title: 'CM', badge: 'Activos', badgeColor:'success', description: 'Contratos de mantenimiento', icon: ['far', 'file-signature'], value: 0 },
        'Invoices': { position: 5, id: 'Invoices', title: 'Facturas', badge: '', badgeColor:'success', description: 'Saldo pendiente', icon: ['fas', 'hand-holding-usd'], value: '0' },
        // 'oc': { position: 6, id: 'oc', title: 'OC PENDIENTES', badge: 'Pendientes', badgeColor:'success', description: 'OS pendientes de OC', icon: ['fas', 'hand-holding-box'], value: 0 }
      },
      tableFields: {
        'Quotes': [
          { key: 'DocNum', label: 'Documento', sortable: true, thStyle: { width: '120px' }, tdClass: 'text-center font-lato font-weight-bold', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'DocDate', label: 'Creación', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'UN', label: 'UN', sortable: true, thStyle: { width: '80px' }, tdClass: 'text-center font-lato text-uppercase', thClass: 'font-lato fs-12 text-uppercase text-center' },
          { key: 'SlpName', label: 'Asesor', sortable: true, thStyle: { width: '230px' }, tdClass: 'text-left font-lato text-uppercase', thClass: 'font-lato fs-12 text-uppercase text-center' },
          { key: 'Title', label: 'Titulo', tdClass: 'font-lato text-uppercase', thClass: 'font-lato text-uppercase fs-13'},
          { key: 'DocTotalSy', label: 'Total', sortable: true, thStyle: { width: '160px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' }
        ],
        'Orders': [
          { key: 'DocNum', label: 'Documento', sortable: true, thStyle: { width: '120px' }, tdClass: 'text-center font-lato font-weight-bold', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'DocDate', label: 'Creación', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'DocDueDate', label: 'Entrega', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'UN', label: 'UN', sortable: true, thStyle: { width: '80px' }, tdClass: 'text-center font-lato text-uppercase', thClass: 'font-lato fs-12 text-uppercase text-center' },
          { key: 'SlpName', label: 'Asesor', sortable: true, thStyle: { width: '230px' }, tdClass: 'text-left font-lato text-uppercase', thClass: 'font-lato fs-12 text-uppercase text-center' },
          { key: 'Title', label: 'Titulo', tdClass: 'font-lato text-uppercase', thClass: 'font-lato text-uppercase fs-13'},
          { key: 'DocTotalSy', label: 'Total', sortable: true, thStyle: { width: '160px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' }
        ],
        'ServiceCalls': [
          { key: 'DocNum', label: 'Documento', sortable: true, thStyle: { width: '120px' }, tdClass: 'text-center font-lato font-weight-bold', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'Status', label: 'Etapa OS', sortable: true, thStyle: { width: '140px' }, tdClass: 'text-center font-lato text-uppercase', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'DocDate', label: 'Creación', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'TargetDate', label: 'Compromiso', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'UN', label: 'UN', sortable: true, thStyle: { width: '80px' }, tdClass: 'text-center font-lato text-uppercase', thClass: 'font-lato fs-12 text-uppercase text-center' },
          { key: 'Title', label: 'Titulo', tdClass: 'font-lato text-uppercase', thClass: 'font-lato text-uppercase fs-13'},
        ],
        'ServiceContracts': [
          { key: 'DocNum', label: 'Contrato', sortable: true, thStyle: { width: '120px' }, tdClass: 'text-center font-lato font-weight-bold', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'DocDate', label: 'Creación', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'SeriesQty', label: 'Cant. equipos', sortable: true, thStyle: { width: '145px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'Title', label: 'Titulo', tdClass: 'font-lato text-uppercase', thClass: 'font-lato text-uppercase fs-13'}
        ],
        'Invoices': [
          { key: 'DocNum', label: 'Documento', sortable: true, thStyle: { width: '120px' }, tdClass: 'text-center font-lato font-weight-bold', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'Title', label: 'Titulo', tdClass: 'font-lato text-uppercase', thClass: 'font-lato text-uppercase fs-13'},
          { key: 'DocDate', label: 'Creación', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'DocDueDate', label: 'Vencimiento', sortable: true, thStyle: { width: '115px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'UN', label: 'UN', sortable: true, thStyle: { width: '80px' }, tdClass: 'text-center font-lato text-uppercase', thClass: 'font-lato fs-12 text-uppercase text-center' },
          { key: 'DocTotalSy', label: 'Total', sortable: true, thStyle: { width: '160px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'PayToDate', label: 'Pagado', sortable: true, thStyle: { width: '160px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' },
          { key: 'OpenTotal', label: 'Saldo', sortable: true, thStyle: { width: '160px' }, tdClass: 'text-center font-lato', thClass: 'font-lato text-uppercase fs-13 text-center' }
        ]
      },
      searchBardPlaceHolders: {
        'Quotes': 'Buscar por número de cotización o titulo',
        'Orders': 'Buscar por número de orden o titulo',
        'ServiceCalls': 'Buscar por número de OS o titulo',
        'ServiceContracts': 'Buscar por número de contrato o titulo',
        'Invoices': 'Buscar por número de factura o titulo'
      },
      lastTwelveMonths: [],
      showDetailModal: false,
      currentDashboard: {},
      searchTerm: ''
    }
  },
  mounted () {
    if (!this.dashboardsState?.lastFetchedAt) this.fetchDashboards()

    this.lastTwelveMonths = this.getLastTwelveMonths()
  },
  computed: {
    ...mapGetters(['clientDashboards']),
    dashboardsState () {
      return this.clientDashboards(this.id)
    },
    formattedDashboards () {
      const { dashboardCards } = this.dashboardsState
      if (
        !this.ready ||
        !dashboardCards ||
        typeof dashboardCards !== 'object' ||
        Array.isArray(dashboardCards) ||
        !dashboardCards.hasOwnProperty('Quotes')
      ) {
        return Object.values(this.dashboardsConfig)
      }

      const { OverdueInvoice: overdueInvoice, ...dashboards } = dashboardCards
      const dashboardArray = Object.entries(dashboards)

      return dashboardArray.map(([type, value]) => {
        const defaultConfig = this.dashboardsConfig[type]
          ? { ...this.dashboardsConfig[type] }
          : {
              ...this.dashboardsConfig['Quotes'],
              title: 'No definido',
              subtitle: 'No definido',
              description: 'No definido',
              icon: ['far', 'info-circle'],
            }

        if (type === 'Invoices') {
          const total = value
          defaultConfig.value = this.formatDollars(Math.abs(total))
          defaultConfig.badge = overdueInvoice === 'Y' ? 'Saldo moroso' : 'Saldo al día'
          defaultConfig.badgeColor = overdueInvoice === 'Y' ? 'danger' : ''
        } else {
          defaultConfig.value = value
        }

        return defaultConfig

      }).sort((a, b) => a.position - b.position)
    },
    fields () {
      if (!this.currentDashboard.id || !this.showDetailModal) return []
      const data = this.tableFields[this.currentDashboard.id]

      return data || []
    },
    detailItems () {
      if (!this.currentDashboard?.id || !this.showDetailModal) return []

      const { dashboardData } = this.dashboardsState

      let data = dashboardData[this.currentDashboard.id]
      if (!data) return []

      const term = this.normalizeText(this.searchTerm)
      if (!term) return data.sort((a, b) => new Date(b.DocDate) - new Date(a.DocDate))

      data = data.filter(item => {
          const docNum = this.normalizeText(item?.DocNum)
          const title = this.normalizeText(item?.Title)

          return docNum?.includes(term) || title?.includes(term)
      })

      return data.sort((a, b) => new Date(b.DocDate) - new Date(a.DocDate))
    }
  },
  methods: {
    fetchDashboards (onClick = false) {
      const { lastFetchedAt } = this.dashboardsState
      const FIVE_MINUTES = 5 * 60 * 1000
      const isExpired = new Date(lastFetchedAt).getTime() > Date.now() - (FIVE_MINUTES)
      if (lastFetchedAt && isExpired) {
        if (onClick) this.$emit('display-alert', { variant: 'info', message: 'Datos actualizados recientemente' })

        return
      }

      this.loadingDashboards = true
      this.error = false
      this.$http.get(`/api/production/clients/${this.id}/fetch_sap_dashboards`)
        .then((res) => {
          // console.log(res.data)
          const { dashboards_data, dashboard_cards, success, dashboard_charts, fetched_at } = res.data
          if (!success) {
            this.$emit('display-alert', { variant: 'danger', message: 'No se pudieron obtener los dashboards de SAP, intente nuevamente' })
            this.error = true
            return
          }
          this.$emit('display-alert', { variant: 'success', message: 'Indicadores actualizados' })
          this.$store.commit('setClientDashboards', { clientId: this.id, data: {
            dashboardCards: dashboard_cards,
            dashboardData: dashboards_data,
            dashboardCharts: dashboard_charts || {},
            lastFetchedAt: fetched_at
          }})
        })
        .catch(e => {
          this.error = true
        })
        .finally(() => this.loadingDashboards = false)
    },
    getLastTwelveMonths () {
      const result = []
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      const today = new Date()

      for (let i = 12; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1)
        const monthName = months[date.getMonth()]
        const shortYear = String(date.getFullYear()).slice(-2)
        result.push(`${monthName}-${shortYear}`)
      }

      return result
    },
    toggleActiveSection (section) {
      this.activeSection = section
    },
    formatDollars (value) {
      value = Number(value)
      var formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      })

      return formatter.format(value)
    },
    openDetailModal (dashboard) {
      this.currentDashboard = dashboard
      this.showDetailModal = true
    },
    onHideDetailModal () {
      this.searchTerm = ''
      this.currentDashboard = {}
    },
    normalizeText (string) {
      return string?.toString()
                    .toLowerCase()
                    .trim()
                    .replace(/\s+/g, ' ')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '') || ''
    },
    rowClass (item) {
      if (!item) return
      if (!item.hasOwnProperty('OpenTotal') || !item.hasOwnProperty('PayToDate')) return
      const total = Number(item.OpenTotal)
      const date = new Date(item.DocDueDate)
      if (total > 0 && new Date() > date) {
        return 'light-red-row'
      }
    }
  }
}
</script>

<style scoped>
.modal-table td {
  white-space: normal;
  word-wrap: break-word;
  word-break: break-all;
}
</style>
